import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ArrowLeft, Plus, SplitSquareHorizontal, Trash2, Check, Combine, Save,
    AlertTriangle, CheckCircle2, ChevronsUpDown, Loader2, Download, RotateCcw
} from 'lucide-react';
import { documentAPI, accountingAPI, documentLinesAPI, exportAPI, downloadBlob } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '../../components/ui/command';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import { formatCurrency } from '../../lib/utils';
import TillSplitNotice from '../../components/documents/TillSplitNotice';

const tmpId = () => 'tmp-' + Math.random().toString(36).slice(2, 10);
const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

const STATUS_BADGE = {
    suggested: { label: 'Suggested', variant: 'outline' },
    edited: { label: 'Edited', variant: 'warning' },
    approved: { label: 'Approved', variant: 'success' },
};

// Reusable chart-account combobox (also used for the contra account).
function AccountPicker({ accounts, value, onSelect, placeholder = 'Account' }) {
    const [open, setOpen] = useState(false);
    const sel = accounts.find((a) => String(a.konto) === String(value));
    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 w-full justify-between font-normal">
                    <span className="truncate">{sel ? `${sel.konto} ${sel.navn}` : (value || placeholder)}</span>
                    <ChevronsUpDown className="h-3 w-3 opacity-50 shrink-0" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="p-0 w-72" align="start">
                <Command>
                    <CommandInput placeholder="Search account…" />
                    <CommandList>
                        <CommandEmpty>No account found.</CommandEmpty>
                        <CommandGroup>
                            {accounts.map((a) => (
                                <CommandItem key={a.konto} value={`${a.konto} ${a.navn}`} onSelect={() => { onSelect(a); setOpen(false); }}>
                                    <span className="font-mono mr-2">{a.konto}</span>{a.navn}
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}

export default function DocumentReviewPage() {
    const { docId } = useParams();
    const navigate = useNavigate();
    const [header, setHeader] = useState(null);
    const [lines, setLines] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [vatCodes, setVatCodes] = useState([]);
    const [documentTypes, setDocumentTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [approving, setApproving] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [selected, setSelected] = useState([]);   // line ids for merge

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const [docRes, linesRes, chartRes, vatRes, clsRes] = await Promise.all([
                documentAPI.getOne(docId),
                documentLinesAPI.getLines(docId),
                accountingAPI.getRealChart(),
                accountingAPI.getVatCodes(),
                accountingAPI.getClassification(),
            ]);
            setHeader({ ...(docRes.data || {}), ...(linesRes.data || {}) });
            setLines(linesRes.data?.lines || []);
            setAccounts(chartRes.data?.accounts || []);
            setVatCodes(vatRes.data?.vat_codes || []);
            setDocumentTypes((clsRes.data?.document_types || []).filter((t) => t.key !== 'bank_statement'));
            setDirty(false); setSelected([]);
        } catch (e) {
            toast.error('Could not load this document for review');
        } finally { setLoading(false); }
    }, [docId]);
    useEffect(() => { load(); }, [load]);

    const docTotal = header?.document_total ?? 0;
    const currency = header?.currency || 'DKK';
    const status = header?.status;
    const isExported = status === 'exported';
    const canExport = status === 'approved' || isExported;
    const validCodes = useMemo(() => new Set(vatCodes.map((v) => v.code)), [vatCodes]);
    const acctByKonto = useMemo(() => Object.fromEntries(accounts.map((a) => [String(a.konto), a])), [accounts]);

    // Live validation — mirrors the server rules for instant feedback (server re-validates on save/approve).
    const val = useMemo(() => {
        const errors = [], warnings = [];
        let sum = 0;
        lines.forEach((l, i) => {
            if (!(l.text || '').trim()) errors.push(`Line ${i + 1}: description required`);
            const amt = Number(l.amount);
            if (l.amount === '' || l.amount == null || Number.isNaN(amt)) errors.push(`Line ${i + 1}: amount must be a number`);
            else sum += amt;
            if (!String(l.account || '').trim()) errors.push(`Line ${i + 1}: account required`);
            if (!(l.date || '').trim()) errors.push(`Line ${i + 1}: date required`);
            const vc = (l.vat_code || '').trim();
            if (!vc) errors.push(`Line ${i + 1}: VAT code required`);          // missing -> blocks
            else if (!validCodes.has(vc)) warnings.push(`Line ${i + 1}: VAT code ${vc} not recognised — check it's valid for this client`);  // unknown -> warn only
            else {
                const dv = acctByKonto[String(l.account)]?.moms;
                if (dv && vc !== dv) warnings.push(`Line ${i + 1}: VAT ${vc} differs from account default ${dv}`);
            }
            if (!String(l.contra_account || '').trim()) warnings.push(`Line ${i + 1}: no contra account (needed for export)`);
        });
        const tol = 0.01 * Math.max(lines.length, 1) + 1e-9;
        const diff = round2(sum - docTotal);
        const balanced = docTotal <= 0 || Math.abs(sum - docTotal) <= tol;
        if (docTotal > 0 && !balanced) errors.push(`Lines sum to ${sum.toFixed(2)} but document total is ${docTotal.toFixed(2)} (off by ${diff.toFixed(2)})`);
        return { valid: errors.length === 0, balanced, sum: round2(sum), diff, errors, warnings };
    }, [lines, docTotal, validCodes, acctByKonto]);

    // Findings the client CANNOT derive, because they depend on the document's extracted VAT
    // figures rather than on the lines: the mixed-rate block is the only one today. Without this
    // the accountant edits a Z-report, sees "All checks pass", presses Approve and is refused by
    // the server with a message they never had a chance to read.
    //
    // The server suppresses that block once the lines genuinely carry more than one VAT code —
    // that is the split being done by hand — so the same condition is mirrored here rather than
    // showing a stale error after the accountant has fixed it themselves.
    const serverErrors = useMemo(() => {
        const distinct = new Set(lines.map((l) => (l.vat_code || '').trim()).filter(Boolean));
        if (distinct.size > 1) return [];
        return (header?.validation?.errors || [])
            .filter((e) => e && e.code === 'mixed_rate' && e.message)
            .map((e) => e.message);
    }, [header, lines]);

    const allErrors = useMemo(() => [...val.errors, ...serverErrors], [val.errors, serverErrors]);

    const setLine = (id, patch) => { setLines((ls) => ls.map((l) => (l.id === id ? { ...l, ...patch } : l))); setDirty(true); };
    const onPickAccount = (id, a) => {
        const cur = lines.find((l) => l.id === id);
        setLine(id, { account: String(a.konto), account_name: a.navn, vat_code: a.moms || cur?.vat_code || '' });
    };
    const addLine = () => {
        setLines((ls) => [...ls, { id: tmpId(), text: '', amount: 0, account: '', account_name: '', vat_code: '', contra_account: '', date: header?.doc_date || '', status: 'edited', source: 'accountant', ai_original: null }]);
        setDirty(true);
    };
    const splitLine = (id) => {
        setLines((ls) => {
            const out = [];
            ls.forEach((l) => {
                if (l.id === id) {
                    const a = round2(Number(l.amount) / 2);
                    const b = round2(Number(l.amount) - a);
                    out.push({ ...l, amount: a, status: 'edited', source: 'accountant' });
                    out.push({ ...l, id: tmpId(), amount: b, text: `${l.text} (split)`, status: 'edited', source: 'accountant', ai_original: null });
                } else out.push(l);
            });
            return out;
        });
        setDirty(true);
    };
    const deleteLine = (id) => { setLines((ls) => ls.filter((l) => l.id !== id)); setSelected((s) => s.filter((x) => x !== id)); setDirty(true); };
    const mergeSelected = () => {
        if (selected.length < 2) return;
        setLines((ls) => {
            const chosen = ls.filter((l) => selected.includes(l.id));
            const first = chosen[0];
            const merged = {
                ...first, id: tmpId(),
                amount: round2(chosen.reduce((s, l) => s + Number(l.amount || 0), 0)),
                text: chosen.map((l) => l.text).filter(Boolean).join(' + '),
                status: 'edited', source: 'accountant', ai_original: null,
            };
            const out = [];
            let inserted = false;
            ls.forEach((l) => {
                if (selected.includes(l.id)) { if (!inserted) { out.push(merged); inserted = true; } }
                else out.push(l);
            });
            return out;
        });
        setSelected([]); setDirty(true);
    };
    const toggleSelect = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

    const save = async () => {
        setSaving(true);
        try {
            const res = await documentLinesAPI.saveLines(docId, lines);
            setLines(res.data?.lines || lines);
            setDirty(false); setSelected([]);
            toast.success('Lines saved');
            return true;
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to save lines');
            return false;
        } finally { setSaving(false); }
    };

    const approveDocument = async () => {
        if (dirty) { const ok = await save(); if (!ok) return; }
        setApproving(true);
        try {
            const res = await documentLinesAPI.approveDocument(docId);
            toast.success(`Approved — bilag ${res.data?.bilagsnummer} (FY ${res.data?.fiscal_year})`);
            await load();
        } catch (e) {
            const d = e.response?.data?.detail;
            toast.error(typeof d === 'string' ? d : (d?.message || 'Could not approve'));
        } finally { setApproving(false); }
    };

    const approveLine = async (id) => {
        if (dirty) { const ok = await save(); if (!ok) return; }
        try { await documentLinesAPI.approveLine(docId, id); await load(); }
        catch (e) { toast.error(e.response?.data?.detail || 'Could not approve line'); }
    };

    const downloadCsv = async () => {
        if (!header?.tenant_id) return;
        setExporting(true);
        try {
            const res = await exportAPI.downloadKassekladde(header.tenant_id, [docId]);
            downloadBlob(res, 'kassekladde.csv');
            toast.success('CSV downloaded — import it into e-conomic');
            await load();   // reflect the 'exported' marker
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Export failed');
        } finally { setExporting(false); }
    };

    const undoExport = async () => {
        if (!header?.tenant_id) return;
        try {
            await exportAPI.unexportDocument(header.tenant_id, docId);
            toast.success('Export reversed');
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || 'Could not reverse export'); }
    };

    const setDocType = async (dt) => {
        try {
            await documentLinesAPI.setDocumentType(docId, dt);
            setHeader((h) => ({ ...h, document_type: dt }));
            toast.success('Document type updated');
        } catch (e) { toast.error(e.response?.data?.detail || 'Could not update document type'); }
    };

    if (loading) {
        return <div className="flex items-center justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
    }

    return (
        <div className="space-y-6" data-testid="document-review-page">
            <Button variant="ghost" size="sm" onClick={() => navigate('/app/documents')} className="-ml-2">
                <ArrowLeft className="h-4 w-4 mr-1" /> Back to Documents
            </Button>

            {/* Header */}
            <Card>
                <CardHeader>
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0">
                            <CardTitle className="truncate">{header?.supplier || header?.filename || 'Document'}</CardTitle>
                            <p className="text-sm text-muted-foreground mt-1">
                                {header?.doc_date ? `${header.doc_date} · ` : ''}{formatCurrency(docTotal, currency)}
                                {header?.filename ? ` · ${header.filename}` : ''}
                            </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
                            {documentTypes.length > 0 && (
                                <Select value={header?.document_type || ''} onValueChange={setDocType}>
                                    <SelectTrigger className="h-8 w-36" data-testid="document-type-select"><SelectValue placeholder="Set type" /></SelectTrigger>
                                    <SelectContent>
                                        {documentTypes.map((t) => (<SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>))}
                                    </SelectContent>
                                </Select>
                            )}
                            {header?.document_type_source && (
                                <Badge variant="outline" className="whitespace-nowrap">type: {header.document_type_source}</Badge>
                            )}
                            {header?.document_type_needs_confirmation && (
                                <Badge variant="warning" className="whitespace-nowrap"><AlertTriangle className="h-3 w-3 mr-1" /> confirm type</Badge>
                            )}
                            {isExported
                                ? <Badge variant="secondary"><Download className="h-3 w-3 mr-1" /> Exported</Badge>
                                : status === 'approved'
                                    ? <Badge variant="success"><CheckCircle2 className="h-3 w-3 mr-1" /> Approved</Badge>
                                    : <Badge variant="outline">In review</Badge>}
                            {header?.bilagsnummer != null && (
                                <Badge variant="secondary">Bilag {header.bilagsnummer}{header?.fiscal_year ? ` · FY ${header.fiscal_year}` : ''}{header?.bilags_provisional ? ' (provisional)' : ''}</Badge>
                            )}
                            {canExport && (
                                <Button variant="outline" size="sm" onClick={downloadCsv} disabled={exporting} data-testid="download-csv">
                                    <Download className="h-4 w-4 mr-1" /> {exporting ? 'Exporting…' : 'Download e-conomic CSV'}
                                </Button>
                            )}
                            {isExported && (
                                <Button variant="ghost" size="sm" onClick={undoExport} title="Reverse export (back to approved)">
                                    <RotateCcw className="h-4 w-4 mr-1" /> Undo export
                                </Button>
                            )}
                        </div>
                    </div>
                </CardHeader>
            </Card>

            {/* Lines */}
            <Card>
                <CardHeader>
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <CardTitle>Lines ({lines.length})</CardTitle>
                        <div className="flex flex-wrap items-center gap-2">
                            <Button variant="outline" size="sm" onClick={addLine}><Plus className="h-4 w-4 mr-1" /> Add line</Button>
                            <Button variant="outline" size="sm" onClick={mergeSelected} disabled={selected.length < 2}><Combine className="h-4 w-4 mr-1" /> Merge ({selected.length})</Button>
                            <Button variant="outline" size="sm" onClick={save} disabled={saving || !dirty} data-testid="save-lines"><Save className="h-4 w-4 mr-1" /> {saving ? 'Saving…' : 'Save'}</Button>
                            {/* Gated on allErrors, not val.valid: the server refuses approval on a
                                mixed-rate block the client cannot compute, so an enabled button
                                here would promise something the save would then reject. */}
                            <Button size="sm" onClick={approveDocument} disabled={approving || allErrors.length > 0} data-testid="approve-doc"><Check className="h-4 w-4 mr-1" /> {approving ? 'Approving…' : 'Approve document'}</Button>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="p-0">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-8"></TableHead>
                                <TableHead className="min-w-[180px]">Text</TableHead>
                                <TableHead className="w-28 text-right">Amount (gross)</TableHead>
                                <TableHead className="min-w-[200px]">Account</TableHead>
                                <TableHead className="w-24">VAT</TableHead>
                                <TableHead className="min-w-[200px]">Contra</TableHead>
                                <TableHead className="w-36">Date</TableHead>
                                <TableHead className="w-28">Status</TableHead>
                                <TableHead className="w-28"></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {lines.map((l) => (
                                <TableRow key={l.id} data-testid={`line-${l.id}`}>
                                    <TableCell><Checkbox checked={selected.includes(l.id)} onCheckedChange={() => toggleSelect(l.id)} /></TableCell>
                                    <TableCell><Input className="h-8" value={l.text || ''} onChange={(e) => setLine(l.id, { text: e.target.value })} placeholder="Description" /></TableCell>
                                    <TableCell>
                                        <Input className="h-8 text-right font-mono" type="number" step="0.01" value={l.amount ?? ''} onChange={(e) => setLine(l.id, { amount: e.target.value === '' ? '' : Number(e.target.value) })} />
                                    </TableCell>
                                    <TableCell><AccountPicker accounts={accounts} value={l.account} onSelect={(a) => onPickAccount(l.id, a)} /></TableCell>
                                    <TableCell>
                                        <Select value={l.vat_code || ''} onValueChange={(v) => setLine(l.id, { vat_code: v })}>
                                            <SelectTrigger className="h-8"><SelectValue placeholder="VAT" /></SelectTrigger>
                                            <SelectContent>
                                                {vatCodes.map((v) => (<SelectItem key={v.code} value={v.code}>{v.code}</SelectItem>))}
                                            </SelectContent>
                                        </Select>
                                    </TableCell>
                                    <TableCell><AccountPicker accounts={accounts} value={l.contra_account} onSelect={(a) => setLine(l.id, { contra_account: String(a.konto) })} placeholder="Contra" /></TableCell>
                                    <TableCell><Input className="h-8" type="date" value={l.date || ''} onChange={(e) => setLine(l.id, { date: e.target.value })} /></TableCell>
                                    <TableCell>
                                        <div className="flex flex-col gap-1">
                                            <Badge variant={(STATUS_BADGE[l.status] || STATUS_BADGE.suggested).variant} className="w-fit">{(STATUS_BADGE[l.status] || STATUS_BADGE.suggested).label}</Badge>
                                            <span className="text-[10px] text-muted-foreground">{l.source === 'accountant' ? 'edited by you' : 'AI'}</span>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-1">
                                            <Button variant="ghost" size="icon" className="h-7 w-7" title="Split line" onClick={() => splitLine(l.id)}><SplitSquareHorizontal className="h-4 w-4" /></Button>
                                            {l.status !== 'approved' && <Button variant="ghost" size="icon" className="h-7 w-7 text-success" title="Approve line" onClick={() => approveLine(l.id)}><Check className="h-4 w-4" /></Button>}
                                            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive hover:bg-destructive/10" title="Delete line" onClick={() => deleteLine(l.id)}><Trash2 className="h-4 w-4" /></Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            {/* Validation panel */}
            <Card>
                <CardContent className="p-5 space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <span className="text-sm font-medium">Validation</span>
                        <div className="flex items-center gap-4 text-sm font-mono">
                            <span>Lines: {formatCurrency(val.sum, currency)}</span>
                            <span>Document: {formatCurrency(docTotal, currency)}</span>
                            <span className={val.balanced ? 'text-success' : 'text-destructive'}>
                                {val.balanced ? 'Balanced' : `Off by ${val.diff.toFixed(2)}`}
                            </span>
                        </div>
                    </div>
                    <TillSplitNotice split={header?.till_split} currency={currency} />
                    {allErrors.length > 0 ? (
                        <ul className="space-y-1">
                            {allErrors.map((e, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm text-destructive"><AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> {e}</li>
                            ))}
                        </ul>
                    ) : (
                        <p className="flex items-center gap-2 text-sm text-success"><CheckCircle2 className="h-4 w-4" /> All checks pass — ready to approve.</p>
                    )}
                    {val.warnings.length > 0 && (
                        <ul className="space-y-1 pt-1 border-t border-border/50">
                            {val.warnings.map((w, i) => (
                                <li key={i} className="flex items-start gap-2 text-xs text-warning"><AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {w}</li>
                            ))}
                        </ul>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
